import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Check, Image as ImageIcon, Loader2, Search, Trash2, Upload, UploadCloud, X,
} from 'lucide-react'
import { BuilderContext } from '../BuilderContextInstance'
import { CategoryAutocomplete } from '../CategoryAutocomplete'
import { mediaDisplaySrc } from './mediaSrc'
import { useMediaLibrary } from './useMediaLibrary'
import { useScrimDismiss } from '../../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../../shared/hooks/useEscapeClose'
import { hasFieldValue } from '../editors/fieldValue'

/**
 * La médiathèque — pop-in plein écran de sélection d'image du builder.
 *
 * Remplace deux sélecteurs : l'ancienne médiathèque sur disque et
 * l'autocomplétion de fichiers du champ `image-drupal`. Un seul stock (des
 * entités `media` du bundle `image`), une seule surface.
 *
 * Pourquoi une pop-in plein écran alors que la règle du builder est « édition
 * en place, pas de modale » : parcourir une bibliothèque n'est pas modifier un
 * champ. C'est la même nature de surface que le catalogue de blocs, qui est
 * déjà un `modal-panel-full` — la règle vise les formulaires de modification,
 * qui restent inline.
 *
 * Redimensionnement : chaque vignette est demandée au point de terminaison
 * `dynamic_image` à la taille de sa case (`{cropBase}/{src}__s_320_320.webp`,
 * + srcset 1x/2x), jamais l'original. Mise à l'échelle SANS recadrage : une
 * bannière très large doit rester lisible, quitte à être cernée de gris. Une source vectorielle arrive sans
 * descripteur de recadrage — il n'y a rien à redimensionner dans un SVG — et
 * retombe sur `url`.
 *
 * Props :
 *  - value      : la sélection courante — un `mid`, une URL, ou null
 *  - onPick     : (item) => void — l'appelant décide de ce qu'il stocke
 *  - onClear    : () => void — RETIRER l'image du champ (facultatif). La
 *                 médiathèque ne sait que choisir : sans cette commande, une
 *                 image posée une fois ne se reprend plus. Le bouton
 *                 n'apparaît que si l'appelant la fournit ET qu'il y a une
 *                 image à retirer. À ne pas confondre avec la suppression du
 *                 média lui-même (rail de droite) : ici on ne touche qu'au
 *                 champ, le fichier reste dans la bibliothèque.
 *  - onClose    : () => void
 *  - title      : intitulé de la pop-in
 *  - siteId     : le site sur lequel ouvrir la grille, et auquel rattacher ce
 *                 qui est chargé. Par défaut celui de la page en cours
 *                 d'édition (contexte du builder) ; un formulaire hors builder
 *                 — les réglages d'un site — le passe explicitement.
 *  - siteName   : son nom, pour le rail « site » (évite une recherche)
 */

/**
 * Boîtes demandées au point de terminaison dynamic_image.
 *
 * Elles collent à la taille RÉELLEMENT rendue, pas à une valeur confortable :
 * une vignette occupe ~150 px et un aperçu de détail ~285 px. Demander 320 et
 * 640 revenait à télécharger deux fois trop d'octets, et le `sizes` annoncé
 * (320px / 640px) mentait au navigateur, qui montait encore d'un cran sur
 * écran haute densité. Le srcset garde le doublement 2x — mais il ne sert
 * plus que là où il est vraiment utile.
 *
 * L'opération est `s` (mise à l'échelle) et non `sc` (échelle PUIS recadrage) :
 * une bannière de 1146×103 recadrée au centre d'un carré ne montre qu'une
 * tranche méconnaissable. On préfère la voir entière, cernée de gris.
 */
const TILE_BOX = 160
const PREVIEW_WIDTH = 320

/** « 1,4 Mo » — le poids d'un fichier, lisible. */
const formatSize = (bytes) => {
  if (!bytes) return ''
  const units = ['o', 'ko', 'Mo']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1).replace('.', ',')} ${units[unit]}`
}

export const MediaLibrary = ({ value, onPick, onClear = null, onClose, title = 'Médiathèque', siteId, siteName }) => {
  const scrim = useScrimDismiss(onClose)
  useEscapeClose(onClose)
  // Le site de la page en cours d'édition. `useContext` plutôt que `useBuilder`
  // (qui lève hors provider) : la médiathèque doit rester montable seule.
  const builder = useContext(BuilderContext)
  const currentSiteId = siteId ?? builder?.page?.siteId ?? null
  const currentSiteName = siteName || builder?.page?.siteName || ''

  // On ouvre sur le site courant : un rédacteur qui travaille sur une page de
  // Corsair n'a rien à faire dans les images de l'Académie. Le rail garde
  // « Toutes les images » pour aller chercher un visuel partagé.
  const lib = useMediaLibrary({ initialSite: currentSiteId, uploadSite: currentSiteId })
  const {
    items, categories, sites, total, libraryTotal, loading, uploading, error, clearError,
    hasMore, loadMore, search, setSearch, category, setCategory, site, setSite,
    upload, updateItem, removeItem, createCategory, searchCategories,
  } = lib

  const [selectedMid, setSelectedMid] = useState(null)
  const [dragging, setDragging] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)
  // Announced to assistive tech after an upload / delete — those change the
  // grid without moving the focus, so nothing else would report them.
  const [announcement, setAnnouncement] = useState('')

  const panelRef = useRef(null)
  const searchRef = useRef(null)
  const fileInputRef = useRef(null)
  // A dragenter on a child fires after the parent's dragleave; counting the
  // enters instead of toggling a boolean keeps the overlay from flickering.
  const dragDepth = useRef(0)

  const selected = useMemo(
    () => items.find(item => item.mid === selectedMid) ?? null,
    [items, selectedMid],
  )

  // Pre-select whatever the field already holds, once its item shows up in the
  // first page of results.
  useEffect(() => {
    if (selectedMid !== null || !items.length) return
    const match = items.find(item => (
      (typeof value === 'object' && value?.mid && item.mid === value.mid)
      || (typeof value === 'string' && value && item.url === value)
    ))
    if (match) setSelectedMid(match.mid)
  }, [items, value, selectedMid])

  // --- Clavier / focus -------------------------------------------------------

  useEffect(() => {
    searchRef.current?.focus()
  }, [])

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return
      // Piège à focus : une pop-in modale ne rend pas la main au canevas
      // derrière elle.
      const focusables = panelRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (!focusables?.length) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [onClose])

  // --- Chargement ------------------------------------------------------------

  const handleFiles = useCallback(async (files) => {
    const created = await upload(files, { categories: category ? [category] : undefined })
    if (created.length) {
      setSelectedMid(created[created.length - 1].mid)
      setAnnouncement(
        created.length === 1
          ? `${created[0].name} ajoutée à la médiathèque.`
          : `${created.length} images ajoutées à la médiathèque.`,
      )
    }
  }, [upload, category])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    handleFiles(e.dataTransfer.files)
  }, [handleFiles])

  const handleDragEnter = useCallback((e) => {
    if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return
    dragDepth.current += 1
    setDragging(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }, [])

  // --- Catégories ------------------------------------------------------------

  const setItemCategories = (item, ids) => updateItem(item.mid, { categories: ids })

  // --- Suppression -----------------------------------------------------------

  const handleDelete = async (item) => {
    const ok = await removeItem(item.mid)
    setPendingDelete(null)
    if (ok) {
      if (selectedMid === item.mid) setSelectedMid(null)
      setAnnouncement(`${item.name} supprimée de la médiathèque.`)
    }
  }

  const railCategories = categories.filter(c => c.id !== 0 || c.count > 0)
  // Un site sans aucune image ne mérite pas une ligne de filtre qui ne rendrait
  // jamais rien — sauf le site courant, qu'on montre même vide pour que la
  // sélection en cours reste lisible.
  const siteRail = sites.filter(s => s.count > 0 || s.id === currentSiteId)

  return createPortal(
    <div className="modal-scrim modal-scrim-full" {...scrim}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="media-library-title"
        className="modal-panel modal-panel-full media-lib"
        onMouseDown={(e) => e.stopPropagation()}
        onDragEnter={handleDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* ── En-tête : titre, recherche, chargement ── */}
        <div className="media-lib-head">
          <h2 id="media-library-title" className="media-lib-title">
            <ImageIcon size={17} aria-hidden="true" />
            {title}
            {site !== null && (
              <span className="media-lib-scope">
                {sites.find(s => s.id === site)?.label
                  ?? (site === currentSiteId ? currentSiteName : null)
                  ?? ('Site #' + site)}
              </span>
            )}
          </h2>

          <div className="media-lib-search">
            <Search size={15} aria-hidden="true" className="media-lib-search-icon" />
            <input
              ref={searchRef}
              id="media-library-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher une image…"
              aria-label="Rechercher une image par nom, nom de fichier ou texte alternatif"
              className="media-lib-search-input"
            />
          </div>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn btn-primary media-lib-upload-btn"
          >
            {uploading
              ? <Loader2 size={15} className="animate-spin" aria-hidden="true" />
              : <Upload size={15} aria-hidden="true" />}
            {uploading ? 'Chargement…' : 'Charger des images'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.ico"
            multiple
            className="sr-only"
            onChange={(e) => { handleFiles(e.target.files); e.target.value = '' }}
          />

          <button
            type="button"
            onClick={onClose}
            className="media-lib-close"
            aria-label="Fermer la médiathèque"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {error && (
          <p role="alert" className="alert alert-danger media-lib-alert">
            {error}
            <button type="button" onClick={clearError} className="media-lib-alert-close" aria-label="Masquer l'erreur">
              <X size={13} aria-hidden="true" />
            </button>
          </p>
        )}

        <p aria-live="polite" className="sr-only">{announcement}</p>

        {/* ── Corps : rail de catégories | grille | détail ── */}
        <div className="media-lib-body">

          <nav className="media-lib-rail" aria-label="Catégories">
            <button
              type="button"
              onClick={() => { setCategory(null); setSite(null) }}
              aria-current={category === null && site === null ? 'true' : undefined}
              className="media-lib-rail-item"
            >
              <span>Toutes les images</span>
              <span className="media-lib-rail-count">{libraryTotal}</span>
            </button>

            {siteRail.length > 0 && (
              <>
                <p className="media-lib-rail-heading">Sites</p>
                {siteRail.map(s => (
                  <button
                    key={`site-${s.id}`}
                    type="button"
                    onClick={() => setSite(site === s.id ? null : s.id)}
                    aria-current={site === s.id ? 'true' : undefined}
                    className="media-lib-rail-item"
                  >
                    <span className="truncate">
                      {s.label}
                      {s.id === currentSiteId && (
                        <span className="media-lib-rail-badge" title="Site de la page en cours d'édition">
                          page
                        </span>
                      )}
                    </span>
                    <span className="media-lib-rail-count">{s.count}</span>
                  </button>
                ))}
                <p className="media-lib-rail-heading">Catégories</p>
              </>
            )}

            {railCategories.map(cat => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setCategory(cat.id)}
                aria-current={category === cat.id ? 'true' : undefined}
                className="media-lib-rail-item"
              >
                <span className="truncate">{cat.label}</span>
                <span className="media-lib-rail-count">{cat.count}</span>
              </button>
            ))}

          </nav>

          <div className="media-lib-main">
            {loading && !items.length && (
              <p className="media-lib-note">
                <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                Chargement de la médiathèque…
              </p>
            )}
            {!loading && !items.length && (
              <p className="media-lib-note">
                {search || category !== null || site !== null
                  ? 'Aucune image ne correspond à cette recherche.'
                  : 'La médiathèque est vide.'}
              </p>
            )}

            <ul className="media-lib-grid">
              {/* La zone de dépôt occupe la première case : toujours visible,
                  sans voler une ligne à la grille, et c'est aussi le bouton de
                  parcours. Le voile plein écran reste, pour déposer ailleurs. */}
              <li>
                <label
                  className="media-lib-drop-tile"
                  data-dragging={dragging ? 'true' : undefined}
                >
                  <input
                    type="file"
                    accept="image/*,.ico"
                    multiple
                    className="sr-only"
                    onChange={(e) => { handleFiles(e.target.files); e.target.value = '' }}
                  />
                  {uploading
                    ? <Loader2 size={22} className="animate-spin" aria-hidden="true" />
                    : <UploadCloud size={22} strokeWidth={1.5} aria-hidden="true" />}
                  <span className="media-lib-drop-tile-title">
                    {uploading ? 'Chargement…' : 'Déposer des images'}
                  </span>
                  <span className="media-lib-drop-tile-hint">ou cliquer pour parcourir</span>
                </label>
              </li>

              {items.map(item => (
                <li key={item.mid}>
                  <MediaTile
                    item={item}
                    selected={selectedMid === item.mid}
                    onSelect={() => setSelectedMid(item.mid)}
                  />
                </li>
              ))}
            </ul>

            {hasMore && (
              <div className="media-lib-more">
                <button type="button" onClick={loadMore} disabled={loading} className="btn btn-ghost">
                  {loading
                    ? <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    : null}
                  Afficher plus ({items.length} / {total})
                </button>
              </div>
            )}
          </div>

          <aside className="media-lib-detail" aria-label="Détail du média sélectionné">
            {!selected ? (
              <p className="media-lib-detail-empty">
                Sélectionnez une image pour la renommer, la classer ou l'utiliser.
              </p>
            ) : (
              <MediaDetail
                item={selected}
                onRename={(name) => updateItem(selected.mid, { name })}
                onAlt={(alt) => updateItem(selected.mid, { alt })}
                onCategories={(ids) => setItemCategories(selected, ids)}
                onCreateCategory={createCategory}
                onSearchCategories={searchCategories}
                pendingDelete={pendingDelete === selected.mid}
                onAskDelete={() => setPendingDelete(selected.mid)}
                onCancelDelete={() => setPendingDelete(null)}
                onConfirmDelete={() => handleDelete(selected)}
              />
            )}
          </aside>
        </div>

        {/* ── Pied : valider la sélection ── */}
        <div className="modal-footer">
          {onClear && hasFieldValue(value) && (
            <button
              type="button"
              onClick={() => { onClear(); onClose() }}
              className="btn btn-ghost mr-auto"
            >
              Retirer l’image
            </button>
          )}
          <button type="button" onClick={onClose} className="btn btn-ghost">Annuler</button>
          <button
            type="button"
            disabled={!selected}
            onClick={() => { if (selected) { onPick(selected); onClose() } }}
            className="btn btn-primary"
          >
            Utiliser cette image
          </button>
        </div>

        {/* ── Voile de dépôt ── */}
        {dragging && (
          <div className="media-lib-dropzone" aria-hidden="true">
            <UploadCloud size={44} strokeWidth={1.5} />
            <p>Déposez vos images pour les ajouter</p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/* ── Vignette ─────────────────────────────────────────────────────────────── */

const MediaTile = ({ item, selected, onSelect }) => {
  const { src, srcSet } = mediaDisplaySrc(item, { op: 's', w: TILE_BOX, h: TILE_BOX })

  return (
    <button
      type="button"
      onClick={onSelect}
      onDoubleClick={onSelect}
      aria-pressed={selected}
      className="media-lib-tile"
      data-selected={selected ? 'true' : undefined}
    >
      <img
        src={src}
        srcSet={srcSet}
        sizes={`${TILE_BOX}px`}
        alt=""
        width={TILE_BOX}
        height={TILE_BOX}
        loading="lazy"
        decoding="async"
        className="media-lib-tile-img"
      />
      <span className="media-lib-tile-name">{item.name}</span>
      {selected && (
        <span className="media-lib-tile-check" aria-hidden="true">
          <Check size={12} strokeWidth={3} />
        </span>
      )}
    </button>
  )
}

/* ── Panneau de détail ────────────────────────────────────────────────────── */

const MediaDetail = ({
  item, onRename, onAlt, onCategories, onCreateCategory, onSearchCategories,
  pendingDelete, onAskDelete, onCancelDelete, onConfirmDelete,
}) => {
  const [name, setName] = useState(item.name)
  const [alt, setAlt] = useState(item.alt)

  // Le panneau suit la sélection : repartir des valeurs du média courant.
  useEffect(() => { setName(item.name); setAlt(item.alt) }, [item.mid, item.name, item.alt])

  const { src, srcSet } = mediaDisplaySrc(item, { op: 's', w: PREVIEW_WIDTH, h: 0 })

  return (
    <div className="media-lib-detail-inner">
      <img
        src={src}
        srcSet={srcSet}
        sizes={`${PREVIEW_WIDTH}px`}
        alt={item.alt || ''}
        className="media-lib-detail-preview"
      />

      <dl className="media-lib-detail-meta">
        <dt>Fichier</dt>
        <dd title={item.filename}>{item.filename}</dd>
        <dt>Dimensions</dt>
        <dd>{item.width ? `${item.width} × ${item.height} px` : '—'}{item.size ? ` · ${formatSize(item.size)}` : ''}</dd>
        <dt>Site</dt>
        <dd>{item.sites?.length ? item.sites.map(s => s.label).join(', ') : 'Partagée'}</dd>
      </dl>

      <div className="media-lib-field">
        <label htmlFor="media-library-name">Nom</label>
        <input
          id="media-library-name"
          type="text"
          value={name}
          disabled={!item.editable}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => { const v = name.trim(); if (v && v !== item.name) onRename(v) }}
          className="input"
        />
        <p className="media-lib-field-hint">Ce nom est ce que cherche la recherche plein texte.</p>
      </div>

      <div className="media-lib-field">
        <label htmlFor="media-library-alt">Texte alternatif</label>
        <input
          id="media-library-alt"
          type="text"
          value={alt}
          disabled={!item.editable}
          onChange={(e) => setAlt(e.target.value)}
          onBlur={() => { if (alt !== item.alt) onAlt(alt) }}
          className="input"
        />
        <p className="media-lib-field-hint">Décrit l'image pour qui ne la voit pas. À laisser vide si l'image est purement décorative.</p>
      </div>

      <div className="media-lib-field">
        <label htmlFor="media-library-categories">Catégories</label>
        <CategoryAutocomplete
          inputId="media-library-categories"
          value={item.categories}
          disabled={!item.editable}
          onChange={onCategories}
          onCreate={onCreateCategory}
          onSearch={onSearchCategories}
        />
        <p className="media-lib-field-hint">
          Tapez pour filtrer ; un nom inconnu peut être créé depuis la liste.
        </p>
      </div>

      {item.deletable && (
        <div className="media-lib-detail-danger">
          {pendingDelete ? (
            <>
              <p>Supprimer « {item.name} » définitivement ?</p>
              <div className="flex gap-2">
                <button type="button" onClick={onConfirmDelete} className="btn btn-danger">Supprimer</button>
                <button type="button" onClick={onCancelDelete} className="btn btn-ghost">Annuler</button>
              </div>
            </>
          ) : (
            <button type="button" onClick={onAskDelete} className="media-lib-detail-delete">
              <Trash2 size={13} aria-hidden="true" />
              Supprimer de la médiathèque
            </button>
          )}
        </div>
      )}
    </div>
  )
}
